import React from 'react';
import { FeaturedCard } from '@components/v5/FeaturedHighlight/index';

const techBadge = (label) => (
  <span
    key={label}
    className="mr-2 mb-2 inline-block rounded-full border border-v5-green-200 px-3 py-1 text-xs font-medium text-gray-700"
  >
    {label}
  </span>
);

const projectItem = ({ name, description }) => (
  <li
    key={name}
    className="flex items-start gap-2 text-sm leading-relaxed text-black"
  >
    <span className="mt-[7px] flex-shrink-0 text-[10px] leading-none text-v5-green-200">
      ▶
    </span>
    <span>
      <span className="font-bold">{name}</span>
      {description && <span> — {description}</span>}
    </span>
  </li>
);

const advancedBox = ({ title, content }) => (
  <div className="mt-6 rounded-xl border border-gray-100 bg-gray-50 p-4">
    <p className="mb-1 text-sm font-bold text-gray-800">{title}</p>
    <p className="text-xs leading-relaxed text-gray-500">{content}</p>
  </div>
);

// Default content — used as a fallback when no `data` prop is supplied
// (e.g. pages that render <ChooseSpecialization /> without wiring it to YAML).
export const DEFAULT_CHOOSE_SPECIALIZATION = {
  badge: 'Choose Your Specialization',
  title:
    'Frontend Developer Track (FDT) or Backend Developer Track (BDT)',
  description:
    'Both tracks share the same core foundation — DSA, Dev Essentials, and AI-powered Software Development. You pick your specialization during the <strong class="text-gray-800">Tech Stack Specialization &amp; Work-like Projects</strong> phase and deepen it further in <strong class="text-gray-800">Advanced Specialization.</strong>',
  footerNote:
    'You can explore both paths during early phases before locking in your specialization with the help of your mentor.',
  cards: [
    {
      badge: 'OPTION 1 - FDT',
      title: 'Fullstack Specialization',
      description:
        'Build products like developers at AirBnB, Amazon & Netflix to specialize in Full-Stack Development.',
      tech: [
        'HTML/CSS',
        'JavaScript',
        'React',
        'Node.js',
        'Express',
        'MongoDB',
        'Material UI',
      ],
      projects: [
        {
          name: 'QTrip Static',
          description: 'AirBnB-like static frontend (HTML, CSS, Bootstrap)',
        },
        {
          name: 'QTrip Dynamic',
          description: 'Add API calls, routing, filters & data persistence',
        },
        {
          name: 'XBoard',
          description:
            'Independent project: Google News-like newsfeed aggregator',
        },
        {
          name: 'QKart Frontend',
          description: 'React-powered Amazon/Flipkart-style shopping app',
        },
        {
          name: 'QTify',
          description:
            'Independent project: Spotify-like audio streaming frontend',
        },
        {
          name: 'QKart Backend',
          description: 'Scalable Node.js/Express backend for Flipkart-like app',
        },
        {
          name: 'XFlix',
          description:
            'Independent project: Netflix-like video streaming backend',
        },
      ],
      advancedTitle: 'Frontend Advanced:',
      advancedContent:
        'Caching, Security, Website Performance Optimization, Redux State Management, Server-Side Rendering with Next.js, CDN, Advanced CSS, SEO',
    },
    {
      badge: 'OPTION 2 - BDT',
      title: 'Backend Specialization',
      description:
        'Go deep on enterprise-grade Java backend systems used at top product companies.',
      tech: [
        'Java',
        'OOP',
        'Spring Boot',
        'MongoDB',
        'REST APIs',
        'Gradle',
        'JUnit',
        'Jackson',
      ],
      projects: [
        {
          name: 'QCalc',
          description:
            'First multi-file Java project: Scientific calculator with OOP',
        },
        {
          name: 'XURL',
          description: 'Independent project: Bit.ly-like URL shortener in Java',
        },
        {
          name: 'QMoney',
          description: 'Zerodha-like visual stock portfolio analyser',
        },
        {
          name: 'XCompany',
          description: 'Object modeling & machine coding company hierarchy',
        },
        {
          name: 'QEats',
          description:
            'Scalable distributed Java backend for Swiggy-like food ordering (Spring Boot)',
        },
        {
          name: 'XMeme',
          description:
            'Independent project: Java backend for Giphy-like meme platform',
        },
      ],
      advancedTitle: 'Backend Advanced:',
      advancedContent:
        'Make QEats scale-able — advanced search features, performance bottleneck solving, REST API performance — with Caching, Redis, JMeter & Multi-threading',
    },
  ],
};

export default function ChooseSpecialization({ data }) {
  const { badge, title, description, footerNote, cards } =
    data && data.cards ? data : DEFAULT_CHOOSE_SPECIALIZATION;

  return (
    <div className="min-h-screen bg-white px-4 py-12 font-manrope">
      {/* Header */}
      {badge && (
        <div className="mb-2 text-center">
          <span className="mb-4 inline-block rounded-full bg-v5-green-200 px-3 py-1 text-[11px] font-bold uppercase text-white">
            {badge}
          </span>
        </div>
      )}
      <h1
        className="mx-auto mb-4 max-w-[800px] text-center font-manrope text-gray-900"
        dangerouslySetInnerHTML={{ __html: title }}
      />

      {description && (
        <p
          className="mx-auto mb-12 max-w-[560px] text-center text-sm leading-[1.7] text-gray-600"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}

      {/* Cards */}
      <div className="mx-auto grid max-w-5xl grid-cols-1 items-start gap-6 pt-4 md:grid-cols-2">
        {cards.map((card) => (
          <FeaturedCard
            key={card.badge || card.title}
            recommended={card.recommended}
            tagLabel={card.tag}
            tagPosition="top-left"
            className="ease cursor-default rounded-2xl border-2 border-v5-green-200 p-7"
          >
            {card.badge && (
              <span className="mb-4 inline-block rounded-full bg-v5-green-200 px-3 py-1 text-[11px] font-bold uppercase text-white">
                {card.badge}
              </span>
            )}
            <h2 className="mb-1 text-2xl font-bold text-gray-900">
              {card.title}
            </h2>
            {card.targetRole && (
              <p className="mb-2 text-sm font-bold text-v5-green-200">
                {card.targetRole}
              </p>
            )}
            {card.description && (
              <p className="mb-4 text-sm leading-relaxed text-gray-500">
                {card.description}
              </p>
            )}

            {card.tech && card.tech.length > 0 && (
              <div className="mb-5">{card.tech.map(techBadge)}</div>
            )}

            {((card.projects && card.projects.length > 0) ||
              card.targetCTC) && (
              <ul className="mb-2 space-y-3">
                {card.projects && card.projects.map(projectItem)}
                {card.targetCTC && (
                  <li className="flex items-start gap-2 text-sm leading-relaxed text-black">
                    <span className="mt-[7px] flex-shrink-0 text-[10px] leading-none text-v5-green-200">
                      ▶
                    </span>
                    <span>
                      <span className="font-bold">Target CTC</span>
                      {' — '}
                      <span className="font-bold text-v5-green-200">
                        {card.targetCTC}
                      </span>
                    </span>
                  </li>
                )}
              </ul>
            )}

            {card.advancedContent &&
              advancedBox({
                title: card.advancedTitle,
                content: card.advancedContent,
              })}
          </FeaturedCard>
        ))}
      </div>

      {/* Footer note */}
      {footerNote && (
        <p className="mx-auto mt-10 max-w-2xl text-center text-sm italic text-v5-neutral-500">
          {footerNote}
        </p>
      )}
    </div>
  );
}
